import { Component, Injector, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { IGs1Segment, IProductExtraParameters, IClvDto } from '../product-info/product-info.model';
import { ProductCardBase } from '../product-card-base';
import { Observable, Subscription } from 'rxjs';
import { formUtil, keep } from 'src/app/util/form-util';
import { UserAction, ProductCardSection, ProductField } from 'src/app/enum';
import { LocaleService, ProductValidity } from 'src/app/_services';
import { LOCAL_STORAGE_KEY } from 'src/app/config';

@Component({
  selector: 'product-custom-card',
  templateUrl: './product-custom.card.html',
  styleUrls: ['./product-custom.card.scss']
})
export class ProductCustomCard extends ProductCardBase<IProductExtraParameters> {

  public UserAction = UserAction;

  public translationsPath = 'cards.products.product_description_fields';

  public get title() { return this.translationsPath + '.title'; }

  public cardSection = ProductCardSection.ExtraParameters;

  public selectedTags: any[];
  public tagsOptions: IClvDto[];

  public isExpandedKey = LOCAL_STORAGE_KEY.PRODUCT_CUSTOM_CARD_OPEN;

  private _changesSubscription: Subscription;

  public hasAnyValue = false;

  @Input()
  public productGs1Segment: IGs1Segment;

  public productGs1SegmentId = () => {
    let res = [];

    if (this.productGs1Segment) {
      res.push(this.productGs1Segment.id);
    }

    return res;
  }

  constructor(injector: Injector, public productValidity: ProductValidity, public localeService: LocaleService) {
    super(injector, 'extraParameters', 'productExtraParametersChanged');
  }

  protected _getNormalizedFormData(data: IProductExtraParameters) {
    return formUtil.transformData(data, {
      parameter1: keep,
      parameter2: keep,
      parameter3: keep,
      parameter4: keep,
      parameter5: keep,
      parameter6: keep,
      parameter7: keep,
      parameter8: keep,
      parameter9: keep,
      parameter10: keep,
      translatableParameter1: keep,
      translatableParameter2: keep,
      translatableParameter3: keep,
      translatableParameter4: keep,
      translatableParameter5: keep,
      tags: keep
    });
  }

  protected _createFormGroup(data: IProductExtraParameters): FormGroup {
    this.selectedTags = data.tags;
    return formUtil.createFormGroup(this._getNormalizedFormData(data));
  }

  protected _createSaveRequest() {
    const form = this.form.value;
    return {
      parameter1: form.parameter1,
      parameter2: form.parameter2,
      parameter3: form.parameter3,
      parameter4: form.parameter4,
      parameter5: form.parameter5,
      parameter6: form.parameter6,
      parameter7: form.parameter7,
      parameter8: form.parameter8,
      parameter9: form.parameter9,
      parameter10: form.parameter10,
      translatableParameter1: form.translatableParameter1 ? Object.assign({}, ...form.translatableParameter1.map((o) => ({ [o.language]: o.value }))) : {},
      translatableParameter2: form.translatableParameter2 ? Object.assign({}, ...form.translatableParameter2.map((o) => ({ [o.language]: o.value }))) : {},
      translatableParameter3: form.translatableParameter3 ? Object.assign({}, ...form.translatableParameter3.map((o) => ({ [o.language]: o.value }))) : {},
      translatableParameter4: form.translatableParameter4 ? Object.assign({}, ...form.translatableParameter4.map((o) => ({ [o.language]: o.value }))) : {},
      translatableParameter5: form.translatableParameter5 ? Object.assign({}, ...form.translatableParameter5.map((o) => ({ [o.language]: o.value }))) : {},
      tags: form.tags?.map(t => t.id)
    };
  }

  protected _saveInternal(req: any): Observable<any> {
    return this._productService.saveExtraParameters(this.productId, req);
  }

  protected get successMessage(): string {
    return this.translationsPath + '.save_successful';
  }

  public get showContent(): boolean {
    return this.edit || this.hasAnyValue;
  }

  public getProductFieldLabel(field: ProductField) {
    const lang = this.localeService.currentLanguage.value;
    const settings = this.productFieldSettings.find(x => x.field == field);
    const value = settings?.translatedName?.find(x => x.language == lang);
    return value?.value;
  }

  public showParameter(field: ProductField) {
    const settings = this.productFieldSettings.find(x => x.field == field);
    const isProductFieldDisabled = settings?.isDisabled || false;

    if (isProductFieldDisabled) {
      return false;
    }

    if (this.edit) {
      return true;
    }

    let value = null;
    let translatableValue = null;

    switch (field) {
      case ProductField.ExtraParameter1: value = this.form.value.parameter1; break;
      case ProductField.ExtraParameter2: value = this.form.value.parameter2; break;
      case ProductField.ExtraParameter3: value = this.form.value.parameter3; break;
      case ProductField.ExtraParameter4: value = this.form.value.parameter4; break;
      case ProductField.ExtraParameter5: value = this.form.value.parameter5; break;
      case ProductField.ExtraParameter6: value = this.form.value.parameter6; break;
      case ProductField.ExtraParameter7: value = this.form.value.parameter7; break;
      case ProductField.ExtraParameter8: value = this.form.value.parameter8; break;
      case ProductField.ExtraParameter9: value = this.form.value.parameter9; break;
      case ProductField.ExtraParameter10: value = this.form.value.parameter10; break;
      case ProductField.TranslatableParameter1: translatableValue = this.form.value.translatableParameter1; break;
      case ProductField.TranslatableParameter2: translatableValue = this.form.value.translatableParameter2; break;
      case ProductField.TranslatableParameter3: translatableValue = this.form.value.translatableParameter3; break;
      case ProductField.TranslatableParameter4: translatableValue = this.form.value.translatableParameter4; break;
      case ProductField.TranslatableParameter5: translatableValue = this.form.value.translatableParameter5; break;
    }

    const hasValue = value ? true : false;
    const hasTranslatableValue = translatableValue && translatableValue.length && translatableValue.some(e => e.value) ? true : false;

    return hasValue || hasTranslatableValue;
  }

  private _getHasAnyValue(form = this.form.value) {
    const { tags } = form;

    this.hasAnyValue = false;

    if (tags?.length > 0) {
      this.hasAnyValue = true;
    } else {
      for (let i = 1; i <= 10; i++) {
        const hasValue = form[`parameter${i}`] ? true : false;
        const translatableParameter = form[`translatableParameter${i}`];
        const hasTranslatableValue = translatableParameter && translatableParameter.length && translatableParameter.some(e => e.value) ? true : false;

        if (hasValue || hasTranslatableValue) {
          this.hasAnyValue = true;
          break;
        }
      }
    }
  }

  private _setTagsOptions() {
    this.tagsOptions = this._formData.productTags
      .filter(x =>
        x.gs1Segments.length == 0 ||
        x.gs1Segments.some(q => this.productGs1SegmentId().includes(q))
      );
  }

  ngOnInit() {
    super.ngOnInit();
    this._getHasAnyValue();
    this._changesSubscription = this.editing.subscribe(x => this._getHasAnyValue());
  }

  ngOnChanges(changes: any) {
    if (changes.productGs1Segment) {
      this._setTagsOptions();
    }
  }

  ngOnDestroy() {
    super.ngOnInit();
    if (this._changesSubscription) {
      this._changesSubscription.unsubscribe();
    }
  }
}
